import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRemoteSalesSnapshot } from '../../reports/infra/reportsRemoteSalesKpiRepo';
import { invalidateReportsCache, reportsCacheKey } from '../../reports/infra/reportsRemoteCache';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import type { SalesSnapshot } from '../infra/salesSnapshotTypes';
import type { ReportsQueryScope } from '../../reports/domain/reportsQueryScope';
import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleChannelFilter, SaleCommissionFilter } from '../domain/salesTypes';

export type SalesListKpis = {
  totalSum: number;
  paidSum: number;
  pendingSum: number;
  voidedCount: number;
  activeCount: number;
  ticketPromedio: number;
};

type Params = {
  branchId: string | null | undefined;
  fromIso: string;
  toIso: string;
  cashierUserIdFilter?: string | undefined;
  /** "Atendió" (primary_staff_user_id) — mismo criterio que la lista. */
  primaryStaffUserIdFilter?: string | undefined;
  /** Solo ventas con cobro pendiente — mismo criterio que la lista. */
  paymentPendingOnly?: boolean;
  /** Con/sin comisión — mismo criterio que la lista (gated por canView). */
  commissionFilter?: SaleCommissionFilter;
  statusFilter: SaleStatusFilter;
  paymentMethodFilter?: string | undefined;
  fiscalStatusFilter: SaleFiscalStatusFilter;
  channelFilter?: SaleChannelFilter;
  queryScope: ReportsQueryScope;
  enabled?: boolean;
};

function snapshotToKpis(snap: SalesSnapshot | null): SalesListKpis | null {
  if (!snap) return null;
  const activeCount = snap.activeCount ?? snap.salesCount;
  const totalSum = snap.salesTotal;
  const pendingSum = snap.pendingPaymentsAmount ?? 0;
  const paidSum = snap.paidAmount ?? Math.max(0, totalSum - pendingSum);
  const voidedCount = snap.voidedCount ?? 0;
  const ticketPromedio = activeCount > 0 ? totalSum / activeCount : 0;
  return { totalSum, paidSum, pendingSum, voidedCount, activeCount, ticketPromedio };
}

export function useSalesListKpis({
  branchId,
  fromIso,
  toIso,
  cashierUserIdFilter,
  primaryStaffUserIdFilter,
  paymentPendingOnly = false,
  commissionFilter = 'all',
  statusFilter,
  paymentMethodFilter,
  fiscalStatusFilter,
  channelFilter,
  queryScope,
  enabled = true,
}: Params) {
  const [kpis, setKpis] = useState<SalesListKpis | null>(null);
  const [loading, setLoading] = useState(false);
  const lastLoadKeyRef = useRef<string>('');

  const loadKey = `${branchId ?? ''}|${fromIso}|${toIso}|${cashierUserIdFilter ?? ''}|${primaryStaffUserIdFilter ?? ''}|${paymentPendingOnly}|${commissionFilter}|${statusFilter}|${paymentMethodFilter ?? ''}|${fiscalStatusFilter}|${channelFilter ?? ''}|${JSON.stringify(queryScope)}`;

  const loadKpis = useCallback(async () => {
    if (!branchId || !enabled) {
      setKpis(null);
      return;
    }
    setLoading(true);
    try {
      const online = await isOnline();
      if (!online) {
        setKpis(null);
        return;
      }
      const snap = await fetchRemoteSalesSnapshot(
        branchId,
        fromIso,
        toIso,
        queryScope,
        {
          statusFilter: statusFilter !== 'all' ? statusFilter : null,
          paymentMethodFilter: paymentMethodFilter ?? null,
          fiscalStatusFilter: fiscalStatusFilter !== 'all' ? fiscalStatusFilter : null,
          channelFilter: channelFilter !== 'all' ? channelFilter : null,
          primaryStaffUserIdFilter: primaryStaffUserIdFilter ?? null,
          paymentPendingOnly,
          commissionFilter: commissionFilter !== 'all' ? commissionFilter : null,
        }
      );
      setKpis(snapshotToKpis(snap));
    } catch (e) {
      console.warn('[useSalesListKpis] loadKpis', e);
      setKpis(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, fromIso, toIso, cashierUserIdFilter, primaryStaffUserIdFilter, paymentPendingOnly, commissionFilter, statusFilter, paymentMethodFilter, fiscalStatusFilter, channelFilter, queryScope, enabled]);

  useEffect(() => {
    if (loadKey === lastLoadKeyRef.current) return;
    lastLoadKeyRef.current = loadKey;
    void loadKpis();
  }, [loadKey, loadKpis]);

  const refreshKpis = useCallback(async () => {
    if (!branchId) return;
    const prefix = reportsCacheKey(['reports_branch_sales_snapshot', branchId]) + '|';
    invalidateReportsCache(prefix);
    lastLoadKeyRef.current = '';
    await loadKpis();
  }, [branchId, loadKpis]);

  return { kpis, loading, refreshKpis };
}
